import React, { useState } from "react";
import { Modal, Button, message } from "antd";
import { clsx } from "clsx";
import {
    BuyerBriefIntake,
    BriefAddon,
    SupplierTypeGroup,
    EvaluationGoal,
} from "../../../types/BuyerBriefTypes";
import SupplierIdentificationStep from "../steps/SupplierIdentificationStep";
import UniversalTopicStep from "../steps/UniversalTopicStep";
import ConditionalTopicStep from "../steps/ConditionalTopicStep";
import AddonSelectionStep from "../steps/AddonSelectionStep";
import ReviewStep from "../steps/ReviewStep";
import { SvgIcon } from "../../../common/components/SvgIcon";
import { UNIVERSAL_TOPICS } from "../topicConfig";
import styles from "../BuyerBriefWizard.module.less";

interface Props {
    open: boolean;
    onClose: () => void;
    onSubmit: (intake: BuyerBriefIntake) => void;
}

const STEP_LABELS = ["Supplier Info", "Universal Topics", "Specific Topics", "Add-ons", "Review"];
const UNIVERSAL_TOPICS_IDS = UNIVERSAL_TOPICS.map((t) => t.id);

const DemoBriefWizard: React.FC<Props> = ({ open, onClose, onSubmit }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [intake, setIntake] = useState<Partial<BuyerBriefIntake>>({
        selectedTopics: UNIVERSAL_TOPICS_IDS,
        selectedAddons: [],
    });

    const updateIntake = (updates: Partial<BuyerBriefIntake>) => {
        setIntake((prev) => ({ ...prev, ...updates }));
    };

    const isStepValid = (): boolean => {
        switch (currentStep) {
            case 0:
                return !!(intake.supplierName && intake.supplierType && intake.evaluationGoal);
            case 1:
            case 2:
            case 3:
                return true;
            case 4:
                return !!(intake.supplierName && intake.supplierType && intake.evaluationGoal);
            default:
                return true;
        }
    };

    const handleNext = () => {
        if (currentStep < STEP_LABELS.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = () => {
        if (!intake.supplierName || !intake.supplierType || !intake.evaluationGoal) {
            message.error("Please complete all required fields.");
            return;
        }

        onSubmit({
            supplierName: intake.supplierName,
            supplierWebsite: intake.supplierWebsite,
            supplierType: intake.supplierType as SupplierTypeGroup,
            evaluationGoal: intake.evaluationGoal as EvaluationGoal,
            selectedTopics: intake.selectedTopics || [],
            selectedAddons: (intake.selectedAddons || []) as BriefAddon[],
        });
        message.success("Brief submitted — research is running in the background.");
        handleReset();
        onClose();
    };

    const handleReset = () => {
        setCurrentStep(0);
        setIntake({ selectedTopics: UNIVERSAL_TOPICS_IDS, selectedAddons: [] });
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return <SupplierIdentificationStep intake={intake} onChange={updateIntake} />;
            case 1:
                return (
                    <UniversalTopicStep
                        selectedTopics={intake.selectedTopics || []}
                        onChange={(topics) => updateIntake({ selectedTopics: topics })}
                    />
                );
            case 2:
                return (
                    <ConditionalTopicStep
                        supplierType={intake.supplierType as SupplierTypeGroup}
                        selectedTopics={intake.selectedTopics || []}
                        onChange={(topics) => updateIntake({ selectedTopics: topics })}
                    />
                );
            case 3:
                return (
                    <AddonSelectionStep
                        selectedAddons={(intake.selectedAddons || []) as BriefAddon[]}
                        onChange={(addons) => updateIntake({ selectedAddons: addons })}
                    />
                );
            case 4:
                return <ReviewStep intake={intake} />;
            default:
                return null;
        }
    };

    return (
        <Modal
            className={styles.wizardModal}
            open={open}
            onCancel={handleClose}
            title="New Buyer Brief"
            width={900}
            closeIcon={<SvgIcon name="close" size={14} />}
            footer={null}
            maskClosable={false}
            maskStyle={{
                background: "rgba(20, 30, 46, 0.95)",
                backdropFilter: "blur(25px)",
                top: "64px",
            }}
            destroyOnClose
        >
            <div className={styles.stepsBar}>
                {STEP_LABELS.map((label, i) => (
                    <span
                        key={label}
                        className={
                            i < currentStep
                                ? clsx(styles.step, styles.stepComplete)
                                : i === currentStep
                                  ? clsx(styles.step, styles.stepCurrent)
                                  : styles.step
                        }
                    >
                        {i < currentStep ? "\u2713 " : ""}
                        {label}
                    </span>
                ))}
            </div>
            <div className={styles.stepContent}>{renderStep()}</div>
            <div className={styles.footerButtons}>
                <div className={styles.footerInner}>
                    <Button
                        disabled={currentStep === 0}
                        onClick={handleBack}
                        size="small"
                        type="link"
                    >
                        Back
                    </Button>
                    <div>
                        {currentStep < STEP_LABELS.length - 1 ? (
                            <Button
                                type="primary"
                                disabled={!isStepValid()}
                                onClick={handleNext}
                                size="small"
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                disabled={!isStepValid()}
                                onClick={handleSubmit}
                                size="small"
                            >
                                Submit Brief
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default DemoBriefWizard;
